import { CheckboxMapping, EndpointData, Locale } from '../types'
import type { LogoFlags, MissingTranslation } from '../components'
import request from '../../../services/request'
import { isChecked } from './helpers'
import { SetStateAction } from 'react'
import { downloadFiles } from '../services'

export const downloadCertificateOwnership = async (
    id: number,
    locale: Locale,
    newFormatCheckboxMappings: CheckboxMapping[],
    isGradientCheckboxMappings: CheckboxMapping[],
    isA3CheckboxMappings: CheckboxMapping[],
    setMissingTranslations: (value: SetStateAction<MissingTranslation[]>) => void,
    setTranslationModalVisible: (value: SetStateAction<boolean>) => void,
    logoFlags?: LogoFlags,
): Promise<void> => {
    const isNewFormat = isChecked(newFormatCheckboxMappings, id)
    const isGradient = isChecked(isGradientCheckboxMappings, id)
    const isA3 = isChecked(isA3CheckboxMappings, id)
    const endpoints: EndpointData[] = [
        {
            url: isNewFormat
                ? '/api/extension/v1/files/certificates/ownership/pdf/side_layout'
                : '/api/extension/v1/files/certificates/ownership/pdf/top_layout',
            defaultExt: 'pdf',
            mime: 'application/pdf',
            withLogoParam: logoFlags?.client_logo ?? false,
            mainLogoId: logoFlags?.main_logo_id,
            isGradient: isGradient,
            isA3: isA3,
        },
        {
            url: '/api/extension/v1/files/certificates/ownership/excel',
            defaultExt: 'xlsx',
            mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            withLogoParam: false,
            toDecode: true,
        },
    ]

    await downloadFiles(endpoints, id, locale, setMissingTranslations, setTranslationModalVisible)
}

export const downloadSticker = async (id: number, withQR?: boolean): Promise<void> => {
    return request
        .get('/api/extension/v1/files/certificates/ownership/svg', {
            params: {
                certificate_id: id,
                with_qr: withQR === undefined ? true : withQR,
            },
            responseType: 'blob',
        })
        .then((response) => {
            const headers = response.headers
            const file = response.data
            const fileRef = document.createElement('a')

            let filename = `sticker_${id}.svg`
            const contentDisposition = headers['content-disposition']
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(
                    /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
                )
                if (filenameMatch && filenameMatch[1]) {
                    // Remove quotes from the extracted filename string
                    filename = filenameMatch[1].replace(/['"]/g, '')
                }
            }

            fileRef.href = URL.createObjectURL(new Blob([file], { type: headers['content-type'] }))
            fileRef.download = filename
            fileRef.target = '_blank'
            fileRef.click()
            URL.revokeObjectURL(fileRef.href)
            fileRef.remove()
        })
}

export const collectOwnershipErrors = async (id: number, locale: Locale) => {
    const endpoints = [
        '/api/extension/v1/files/ownership_certificate',
        '/api/extension/v1/files/certificates/ownership/excel/new',
    ]
    const userMissing: string[] = []
    let operatorError = false

    for (const url of endpoints) {
        try {
            await request.get(url, {
                params: { certificate_id: id, locale },
                responseType: 'blob',
            })
        } catch (err: any) {
            const res = JSON.parse(await err.text())
            if (res?.detail?.user_errors?.length > 0 && locale === 'en') {
                const text_data: string[] = await res.detail.user_errors
                if (text_data) {
                    userMissing.push(...text_data)
                }
            } else if (res?.detail?.operator_errors?.length > 0 && locale === 'en') {
                operatorError = true
            }
        }
    }

    return { userMissing: [...new Set(userMissing)], operatorError }
}
